/* eslint-disable @typescript-eslint/no-floating-promises, consistent-return */
import isError from 'lodash/isError';
import React from 'react';
import { VideoEffectStore } from '../store/videoEffectModel';
import { BackgroundImage } from '../videoEffectTypes';
import { backgroundCache } from './videoBackgroundCache';

interface UseVirtualBackgroundsResult {
  backgrounds: BackgroundImage[];
  isInitialLoading: boolean;
  refreshError: Error | null;
}

export function useVirtualBackgrounds(): UseVirtualBackgroundsResult {
  const fetchImages = VideoEffectStore.useStoreActions((actions) => actions.fetchVitualBackgrounds);

  const [backgrounds, setBackgrounds] = React.useState<BackgroundImage[]>(() => backgroundCache.get() ?? []);
  const [isInitialLoading, setIsInitialLoading] = React.useState<boolean>(() => backgroundCache.get() === null);
  const [refreshError, setRefreshError] = React.useState<Error | null>(null);

  const fetchRef = React.useRef(fetchImages);
  fetchRef.current = fetchImages;

  React.useEffect(() => {
    if (!backgroundCache.isStale()) return;

    let cancelled = false;

    const revalidate = async () => {
      try {
        const response = await fetchRef.current();
        if (cancelled) return;

        const images: BackgroundImage[] =
          response?.data?.preExistingImages?.map((img) => ({
            id: img.id,
            label: img.id,
            url: img.url,
            imageElement: undefined,
            preExisting: true,
            filePath: '',
          })) ?? [];

        backgroundCache.set(images);
        setBackgrounds(images);
        setRefreshError(null);
      } catch (err) {
        // istanbul ignore next
        if (cancelled) return;
        if (backgroundCache.get() === null) {
          setRefreshError(isError(err) ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setIsInitialLoading(false);
      }
    };

    revalidate();

    return () => {
      cancelled = true;
    };
  }, []);

  return { backgrounds, isInitialLoading, refreshError };
}
